import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { Loader2, Sparkles, LogOut, Plus } from "lucide-react";
import {
  login, me, setToken, clearToken, getToken, apiError,
  adminListPosts, adminSavePost, adminSetStatus, adminDeletePost, adminGenerateBatch, adminBatchStatus, adminImprovePost,
} from "@/lib/blogApi";
import { AdminMessages } from "@/components/web/AdminMessages";

const STATUS_LABEL = { draft: "Draft", pending_review: "Pending Review", approved: "Approved", published: "Published", needs_attention: "Needs Attention" };
const STATUS_COLOR = { draft: "text-zinc-400 border-zinc-600", pending_review: "text-amber-400 border-amber-600", approved: "text-sky-400 border-sky-600", published: "text-[#00C5FE] border-[#00C5FE]/50", needs_attention: "text-red-400 border-red-600" };
const istTime = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" }) + " IST" : "";
const EMPTY = { title: "", slug: "", excerpt: "", content: "", featured_image: "", category: "Link Safety", tags: [], author: "LinkExpln Team", seo_title: "", seo_description: "", focus_keyword: "" };

const Field = ({ label, children }) => (
  <div>
    <label className="ls-overline block mb-2">{label}</label>
    {children}
  </div>
);
const inputCls = "w-full bg-black border border-white/15 px-4 py-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:border-transparent";

export default function Admin() {
  const [user, setUser] = useState(undefined); // undefined=checking, false=login, obj=in
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(false);
  const [section, setSection] = useState("posts");
  const [batchCount, setBatchCount] = useState(3);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState("");

  const refresh = useCallback((f) => {
    adminListPosts(f).then(setPosts).catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(() => {
    if (!getToken()) return setUser(false);
    me().then(setUser).catch(() => { clearToken(); setUser(false); });
  }, []);
  useEffect(() => { if (user) refresh(filter); }, [user, filter, refresh]);

  useEffect(() => {
    if (!batchRunning) return;
    const t = setInterval(async () => {
      try {
        const s = await adminBatchStatus();
        if (s.status === "running") {
          setBatchProgress(`${s.done_count || 0}/${s.total || "?"}`);
          refresh(filter);
        } else if (s.status === "done") {
          setBatchRunning(false);
          setBatchProgress("");
          const ok = (s.results || []).filter((r) => r.status === "success").length;
          const bad = (s.results || []).length - ok;
          toast.success(`Batch finished: ${ok} draft(s) created${bad ? `, ${bad} failed/skipped` : ""} — all in Pending Review`);
          (s.results || []).filter((r) => r.status !== "success").forEach((r) => toast.error(`"${r.topic}": ${r.error}`));
          refresh(filter);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(t);
  }, [batchRunning, filter, refresh]);

  const startBatch = async () => {
    try {
      await adminGenerateBatch(batchCount);
      setBatchRunning(true);
      setBatchProgress(`0/${batchCount}`);
      toast.info(`Generating ${batchCount} AI draft(s) with featured images — this can take a few minutes.`);
    } catch (err) { toast.error(apiError(err)); }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await login(creds.email, creds.password);
      setToken(data.token);
      setUser(data);
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); okMsg && toast.success(okMsg); refresh(filter); }
    catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const savePost = () =>
    act(async () => {
      const saved = await adminSavePost({ ...editing, tags: typeof editing.tags === "string" ? editing.tags.split(",").map((t) => t.trim()).filter(Boolean) : editing.tags });
      setEditing(saved);
    }, "Saved");

  if (user === undefined) return <div className="min-h-screen grid place-items-center"><p className="font-mono-ls text-xs text-zinc-400">CHECKING SESSION…</p></div>;

  return (
    <>
      <Helmet>
        <title>Admin — LinkExpln</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Toaster theme="dark" position="bottom-right" richColors />

      {user === false ? (
        <section className="min-h-screen grid place-items-center px-5">
          <form onSubmit={doLogin} data-testid="admin-login-form" className="w-full max-w-sm border border-white/10 bg-[#0A121D] p-8 space-y-5">
            <div className="flex items-center gap-2.5 mb-2">
              <img src="/brand-shield-mark.png" alt="LinkExpln logo" className="h-9 w-9 object-contain" />
              <span className="font-heading font-black tracking-tight text-lg text-white">LINKEXPLN<span className="text-[#00C5FE] text-xs align-super">™</span> ADMIN</span>
            </div>
            <Field label="Email">
              <input data-testid="admin-email-input" type="email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} className={inputCls} placeholder="admin@linkexpln.com" />
            </Field>
            <Field label="Password">
              <input data-testid="admin-password-input" type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} className={inputCls} placeholder="••••••••" />
            </Field>
            <button data-testid="admin-login-button" disabled={busy} className="w-full btn-brand disabled:opacity-60 text-black font-heading font-bold py-3 transition-colors duration-200">
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </section>
      ) : (
        <section className="min-h-screen py-10">
          <div className="max-w-6xl mx-auto px-5 sm:px-8">
            <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <img src="/brand-shield-mark.png" alt="LinkExpln logo" className="h-8 w-8 object-contain" />
                <h1 data-testid="admin-dashboard-heading" className="font-heading font-black text-2xl text-white">Blog Admin</h1>
              </div>
              <div className="flex flex-wrap gap-3">
                <button data-testid="admin-new-post-button" onClick={() => { setEditing({ ...EMPTY }); setPreview(false); }} className="inline-flex items-center gap-2 btn-brand text-black font-heading font-bold text-sm px-4 py-2.5">
                  <Plus size={15} /> New Post
                </button>
                <div className="flex items-center gap-2">
                  <select data-testid="admin-batch-count" value={batchCount} onChange={(e) => setBatchCount(Number(e.target.value))}
                    className="bg-black border border-[#00C5FE]/40 text-[#00C5FE] text-sm px-2 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#00C5FE]">
                    {[1, 3, 5, 10].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <button data-testid="admin-generate-button" disabled={batchRunning}
                    onClick={startBatch}
                    className="inline-flex items-center gap-2 border border-[#00C5FE]/40 text-[#00C5FE] font-heading font-semibold text-sm px-4 py-2.5 hover:bg-[#00C5FE]/10 transition-colors duration-200 disabled:opacity-60">
                    {batchRunning ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                    {batchRunning ? `Generating ${batchProgress}…` : "Generate AI Drafts"}
                  </button>
                </div>
                <button data-testid="admin-logout-button" onClick={() => { clearToken(); setUser(false); }} className="inline-flex items-center gap-2 border border-white/15 text-zinc-300 text-sm px-4 py-2.5 hover:text-white transition-colors duration-200">
                  <LogOut size={15} /> Logout
                </button>
              </div>
            </div>

            <div className="mt-6 flex gap-2 border-b border-white/10 pb-4">
              {[["posts", "Blog Posts"], ["messages", "Messages"]].map(([s, label]) => (
                <button key={s} data-testid={`admin-section-${s}`} onClick={() => setSection(s)}
                  className={`px-4 py-2 font-heading font-bold text-sm border transition-colors duration-200 ${section === s ? "btn-brand text-black border-transparent" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                  {label}
                </button>
              ))}
            </div>

            {section === "messages" && <AdminMessages />}

            {section === "posts" && (<>
            <div className="mt-6 flex flex-wrap gap-2">
              {["", "draft", "pending_review", "approved", "published", "needs_attention"].map((s) => (
                <button key={s} data-testid={`admin-filter-${s || "all"}`} onClick={() => setFilter(s)}
                  className={`px-3 py-1.5 font-mono-ls text-[10px] tracking-[0.12em] uppercase border transition-colors duration-200 ${filter === s ? "btn-brand text-black border-transparent" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                  {s ? STATUS_LABEL[s] : "All"}
                </button>
              ))}
            </div>

            <div className="mt-6 space-y-px bg-white/10 border border-white/10">
              {posts.length === 0 && <p data-testid="admin-empty-list" className="bg-[#0A121D] p-6 text-zinc-400 text-sm">No posts in this view.</p>}
              {posts.map((p) => (
                <div key={p.id} data-testid={`admin-post-row-${p.slug}`} className="bg-[#0A121D] p-5 flex flex-wrap items-center gap-4">
                  {p.featured_image ? (
                    <img src={p.featured_image} alt="" loading="lazy" className="h-12 w-20 object-cover border border-white/10 shrink-0" />
                  ) : (
                    <div className="h-12 w-20 border border-white/10 bg-black grid place-items-center shrink-0"><span className="font-mono-ls text-[8px] text-zinc-600">NO IMG</span></div>
                  )}
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-heading font-semibold text-white">{p.title}{p.revision_of && <span className="ml-2 font-mono-ls text-[9px] tracking-[0.12em] text-amber-400 border border-amber-600 px-1.5 py-0.5 align-middle">REVISION</span>}</p>
                    <p className="font-mono-ls text-[10px] tracking-[0.12em] text-zinc-400 mt-1">/blog/{p.slug} · {p.category} · {p.source === "ai_generated" ? "AI draft" : "manual"} · {(p.created_at || "").slice(0, 10)}</p>
                    {p.status === "pending_review" && p.review_deadline && (
                      <p data-testid={`deadline-${p.slug}`} className="font-mono-ls text-[10px] tracking-[0.12em] text-amber-400 mt-1">
                        AUTO-PUBLISHES {istTime(p.review_deadline)} unless you act
                      </p>
                    )}
                    {p.status === "needs_attention" && (
                      <p data-testid={`attention-${p.slug}`} className="font-mono-ls text-[10px] tracking-[0.12em] text-red-400 mt-1">
                        FAILED: {(p.quality_checks || []).filter((c) => !c.passed).map((c) => c.name).join(", ") || "quality checks"}
                      </p>
                    )}
                  </div>
                  <span className={`px-2.5 py-1 border font-mono-ls text-[10px] tracking-[0.12em] uppercase ${STATUS_COLOR[p.status]}`}>{STATUS_LABEL[p.status]}</span>
                  <div className="flex flex-wrap gap-2">
                    <button data-testid={`admin-edit-${p.slug}`} onClick={() => { setEditing({ ...p, tags: p.tags || [] }); setPreview(false); window.scrollTo(0, 0); }} className="text-xs text-zinc-300 hover:text-[#00C5FE] underline underline-offset-4 transition-colors duration-200">Edit</button>
                    {p.status === "draft" && <button data-testid={`admin-submit-${p.slug}`} onClick={() => act(() => adminSetStatus(p.id, "submit"), "Submitted for review")} className="text-xs text-amber-400 hover:text-white underline underline-offset-4">Submit</button>}
                    {(p.status === "pending_review" || p.status === "draft") && <button data-testid={`admin-approve-${p.slug}`} onClick={() => act(() => adminSetStatus(p.id, "approve"), "Approved")} className="text-xs text-sky-400 hover:text-white underline underline-offset-4">Approve</button>}
                    {p.status === "pending_review" && <button data-testid={`admin-reject-${p.slug}`} onClick={() => act(() => adminSetStatus(p.id, "reject"), "Rejected to draft")} className="text-xs text-red-400 hover:text-white underline underline-offset-4">Reject</button>}
                    {p.status === "approved" && <button data-testid={`admin-publish-${p.slug}`} onClick={() => act(() => adminSetStatus(p.id, "publish"), "Published")} className="text-xs text-[#00C5FE] hover:text-white underline underline-offset-4">Publish</button>}
                    {p.status === "published" && <button data-testid={`admin-unpublish-${p.slug}`} onClick={() => act(() => adminSetStatus(p.id, "unpublish"), "Unpublished")} className="text-xs text-zinc-300 hover:text-white underline underline-offset-4">Unpublish</button>}
                    {p.status === "published" && !p.revision_of && <button data-testid={`admin-improve-${p.slug}`} onClick={() => { toast.info("Generating improved version — takes 1-2 minutes…"); act(() => adminImprovePost(p.id), "Improvement draft created — review it in Pending Review"); }} className="text-xs text-emerald-400 hover:text-white underline underline-offset-4">Improve</button>}
                    <button data-testid={`admin-delete-${p.slug}`} onClick={() => window.confirm("Delete this post permanently?") && act(() => adminDeletePost(p.id), "Deleted")} className="text-xs text-red-500 hover:text-red-300 underline underline-offset-4">Delete</button>
                  </div>
                </div>
              ))}
            </div>

            {editing && (
              <div data-testid="admin-editor" className="mt-10 border border-white/10 bg-[#0A121D] p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading font-bold text-lg text-white">{editing.id ? "Edit Post" : "New Post"}</h2>
                  <div className="flex gap-3">
                    <button data-testid="admin-preview-toggle" onClick={() => setPreview(!preview)} className="text-xs text-zinc-300 hover:text-[#00C5FE] underline underline-offset-4">{preview ? "Edit mode" : "Preview"}</button>
                    <button data-testid="admin-editor-close" onClick={() => setEditing(null)} className="text-xs text-zinc-400 hover:text-white underline underline-offset-4">Close</button>
                  </div>
                </div>
                {preview ? (
                  <div className="prose-preview">
                    <h1 className="font-heading font-black text-3xl text-white mb-4">{editing.title}</h1>
                    <p className="text-zinc-400 mb-6">{editing.excerpt}</p>
                    <pre className="whitespace-pre-wrap text-zinc-300 text-sm leading-relaxed font-sans">{editing.content}</pre>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Title"><input data-testid="editor-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inputCls} /></Field>
                    <Field label="Slug"><input data-testid="editor-slug" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className={inputCls} placeholder="auto-generated from title" /></Field>
                    <Field label="Category"><input data-testid="editor-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inputCls} /></Field>
                    <Field label="Tags (comma-separated)"><input data-testid="editor-tags" value={Array.isArray(editing.tags) ? editing.tags.join(", ") : editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} className={inputCls} /></Field>
                    <Field label="Featured image URL">
                      <input data-testid="editor-image" value={editing.featured_image} onChange={(e) => setEditing({ ...editing, featured_image: e.target.value })} className={inputCls} placeholder="https://…" />
                      {editing.featured_image && <img data-testid="editor-image-preview" src={editing.featured_image} alt="Featured preview" className="mt-3 h-28 object-cover border border-white/10" />}
                    </Field>
                    <Field label="Author"><input data-testid="editor-author" value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} className={inputCls} /></Field>
                    <div className="md:col-span-2"><Field label="Excerpt"><textarea data-testid="editor-excerpt" rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className={inputCls} /></Field></div>
                    <div className="md:col-span-2"><Field label="Content (Markdown)"><textarea data-testid="editor-content" rows={14} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} className={`${inputCls} font-mono-ls`} /></Field></div>
                    <Field label="SEO Title"><input data-testid="editor-seo-title" value={editing.seo_title} onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })} className={inputCls} /></Field>
                    <Field label="Focus Keyword"><input data-testid="editor-keyword" value={editing.focus_keyword} onChange={(e) => setEditing({ ...editing, focus_keyword: e.target.value })} className={inputCls} /></Field>
                    <div className="md:col-span-2"><Field label="SEO Description"><textarea data-testid="editor-seo-desc" rows={2} value={editing.seo_description} onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })} className={inputCls} /></Field></div>
                  </div>
                )}
                {(editing.quality_checks || editing.sources) && (
                  <div data-testid="editor-quality-panel" className="mt-6 border-t border-white/10 pt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                    {editing.quality_checks && (
                      <div>
                        <p className="ls-overline mb-2">Quality checks</p>
                        {editing.quality_checks.map((c) => (
                          <p key={c.name} className={`font-mono-ls text-[11px] leading-relaxed ${c.passed ? "text-emerald-400" : "text-red-400"}`}>
                            {c.passed ? "✓" : "✗"} {c.name}{c.detail ? ` — ${c.detail}` : ""}
                          </p>
                        ))}
                      </div>
                    )}
                    {editing.sources && editing.sources.length > 0 && (
                      <div>
                        <p className="ls-overline mb-2">Research sources</p>
                        {editing.sources.map((s, i) => (
                          <p key={i} className="font-mono-ls text-[11px] leading-relaxed text-zinc-400 break-all">
                            <span className={s.reachable ? "text-emerald-400" : "text-red-400"}>{s.reachable ? "✓" : "✗"}</span> {s.publisher || s.title}: {s.url}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <button data-testid="editor-save-button" disabled={busy} onClick={savePost} className="mt-6 btn-brand disabled:opacity-60 text-black font-heading font-bold px-6 py-3 transition-colors duration-200">
                  {busy ? "Saving…" : "Save"}
                </button>
              </div>
            )}
            </>)}
          </div>
        </section>
      )}
    </>
  );
}
